import React, { useState } from 'react';
import { Menu, UnstyledButton, Avatar, Text, Divider } from '@mantine/core';
import { IconChevronDown, IconCheck, IconPlus } from '@tabler/icons-react';
import { listAccounts, switchToAccount, getActiveAccount } from './../authStorage.js';

// Instagram-style account switcher. A compact chevron that opens a
// Menu listing every logged-in account; one tap switches, "+ Add
// account" takes the user into the login form in additive mode.
//
// After switching, we hard-reload. The simpler alternative — just
// calling updateMyUserInfo() — leaves per-account caches
// (sessionStorage scroll positions, drafts, notification counts)
// pointing at the previous user. A reload is cheap and correct.
//
// After "+ Add account", we scroll the login form into view. The
// form itself writes new tokens via setActiveToken(), which appends
// instead of overwriting, so the previous account survives.
const AccountSwitcher = ({ language = 'ru' }) => {
  const [opened, setOpened] = useState(false);
  const accounts = listAccounts();
  const active = getActiveAccount();
  const t = (ru, en) => (language === 'ru' ? ru : en);

  // Hide the chevron entirely if there's nothing meaningful to
  // choose between — a single-account user doesn't need a dropdown
  // in their chrome. The "+ Add account" affordance is also offered
  // from the inline Add-account button on the My library page, so
  // discoverability doesn't suffer.
  if (!active) return null;

  const handleSwitch = (id) => {
    if (id === active.id) { setOpened(false); return; }
    switchToAccount(id);
    // Hard-reload: clean slate for every per-account cache + fresh
    // SSE / WebSocket re-auth. Keep the current path so a switch on
    // /notifications stays on /notifications (now showing the other
    // user's feed).
    window.location.reload();
  };

  const handleAdd = () => {
    setOpened(false);
    // Dedicated /addAccount route — a standalone login form that
    // calls setActiveToken() on success, which appends to the
    // stored accounts list instead of overwriting. The current
    // account stays signed in throughout.
    window.location.href = '/addAccount';
  };

  return (
    <Menu
      opened={opened}
      onChange={setOpened}
      position="bottom-end"
      shadow="md"
      width={240}
      withinPortal
    >
      <Menu.Target>
        <UnstyledButton
          aria-label={t('Переключить аккаунт', 'Switch account')}
          style={{
            width: 24,
            height: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 999,
            color: 'var(--mantine-color-dimmed)',
          }}
        >
          <IconChevronDown size={16} />
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>{t('Аккаунты', 'Accounts')}</Menu.Label>
        {accounts.map((a) => {
          const isActive = a.id === active.id;
          return (
            <Menu.Item
              key={a.id}
              onClick={() => handleSwitch(a.id)}
              leftSection={<Avatar src={a.avatarUrl} size={24} radius="xl" />}
              rightSection={isActive ? <IconCheck size={14} style={{ color: 'var(--mantine-color-indigo-filled)' }} /> : null}
            >
              <Text size="sm" fw={isActive ? 600 : 400} truncate>
                {a.userName || t('Без имени', 'Unnamed')}
              </Text>
            </Menu.Item>
          );
        })}
        <Divider />
        <Menu.Item onClick={handleAdd} leftSection={<IconPlus size={16} />}>
          <Text size="sm">{t('Добавить аккаунт', 'Add account')}</Text>
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
};

export default AccountSwitcher;
